package com.trevorwiebe.timesheet.mileage.domain.model

import kotlinx.datetime.LocalDateTime

const val ODOMETER_WARNING_THRESHOLD = 500L

// Declaration order defines display order in filters and pickers on all platforms
enum class TripType { LOADING, UNLOADING, START_END, FUEL, STATE_LINE, SERVICE, OTHER }

fun TripType.displayLabel(): String = when (this) {
    TripType.START_END -> "Start/End"
    else -> name.replace('_', ' ')
        .lowercase()
        .replaceFirstChar(Char::titlecase)
}
sealed class TripTypeData {
    data class StateLine(val fromState: String, val toState: String, val highway: String) : TripTypeData()
    data class Fuel(
        val locationId: String,
        val gallons: Double,
        val receiptImageUrl: String?
    ) : TripTypeData()
    data class Loading(val locationId: String, val notes: String) : TripTypeData()
    data class Unloading(val locationId: String, val notes: String) : TripTypeData()
    data class Service(val serviceLocation: String) : TripTypeData()
    data class Other(val notes: String, val locationId: String? = null) : TripTypeData()
    data class StartEnd(val location: String) : TripTypeData()
}

data class MileageEntry(
    val id: String,
    val date: LocalDateTime,
    val employeeId: String,
    val equipmentId: String,
    val trailerId: String?,
    val mileageValue: Long,
    val tripType: TripType,
    val tripTypeData: TripTypeData
)

/** True when this entry carries a non-empty free-text note (Loading/Unloading/Other trips). */
val MileageEntry.hasNotes: Boolean
    get() = when (val data = tripTypeData) {
        is TripTypeData.Loading -> data.notes.isNotBlank()
        is TripTypeData.Unloading -> data.notes.isNotBlank()
        is TripTypeData.Other -> data.notes.isNotBlank()
        else -> false
    }

/** Name of the saved Location tied to this entry, or null when the trip type has none. */
fun MileageEntry.locationName(locations: List<Location>): String? {
    val locationId = when (val data = tripTypeData) {
        is TripTypeData.Fuel -> data.locationId
        is TripTypeData.Loading -> data.locationId
        is TripTypeData.Unloading -> data.locationId
        is TripTypeData.Other -> data.locationId
        else -> null
    }
    if (locationId.isNullOrBlank()) return null
    return locations.firstOrNull { it.id == locationId }?.name?.takeIf { it.isNotBlank() }
}
